import React, {useEffect} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {setAuth} from '../../store/authSlice';
import { RootState } from '../../store/rootReducer';
import {useIsAuthenticated, useMsal} from "@azure/msal-react";
import {InteractionStatus} from "@azure/msal-browser";
import Authenticating from "./Authenticating";
import {loginRequest, msalConfig} from "../../authConfig";
import {SUPPORT_LOGIN_PATH} from "../../logic/supportSession";
import {redirectIfInteractionRequired} from "../../logic/authRedirect";

const AuthHandler: React.FC = (props) => {
    const logoutMessage = useSelector((state: RootState) => state.settings.loggingOutError);

    const dispatch = useDispatch();
    const {instance, accounts, inProgress} = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const auth = useSelector((state: RootState) => state.auth);
    const queryString = window.location.search;
    const path = window.location.pathname;
    const urlParams = new URLSearchParams(queryString);
    const sessionKey = urlParams.get('sessionKey');
    
    const needCredentials = () => {
        // A support tab is signed in by its support session, never by the person's own MSAL account.
        if(sessionKey || auth.sessionKey || path === '/embedded/login' || path === SUPPORT_LOGIN_PATH || auth.supportSession) { return false;}
        return !(auth.token && isAuthenticated);
    }
    
    useEffect(() => {
        if (!instance || inProgress === InteractionStatus.Startup) {
            return;
        }
        if (inProgress === InteractionStatus.HandleRedirect || inProgress === InteractionStatus.Login){
            return;
        }
        const account = instance.getActiveAccount() ?? accounts[0]
        if (needCredentials() && account) {
            const accessTokenRequest = {
                scopes: ['openid', 'profile', 'email', 'offline_access', msalConfig.auth.clientId],
                account: account,
            };
            instance
                .acquireTokenSilent(accessTokenRequest)
                .then((accessTokenResponse) => {
                    // Acquire token silent success
                    const accessToken = accessTokenResponse.accessToken;
                    const idToken = accessTokenResponse.idToken

                    if(idToken && account && account.idTokenClaims && account.idTokenClaims['name']){
                        dispatch(setAuth({
                            token: idToken,
                            user: account.idTokenClaims['name'],
                            accessToken: accessToken
                        }));
                    }
                })
                .catch((error) => {
                    console.log(error);
                    // Through the shared helper so this redirect counts as "redirecting" too: requests already in
                    // flight then fail quietly instead of reaching Sentry, and a second redirect isn't started.
                    redirectIfInteractionRequired(instance, accessTokenRequest, error);
                });
        }
        else if(needCredentials()) {
            instance.loginRedirect(loginRequest);
        }
    }, [instance, accounts, inProgress, auth.token]);



    /*
     * Once a session is already up, routine background MSAL activity - a silent token renewal,
     * an SSO check - has to stay invisible: it is not a "signing in" moment for whoever is
     * already using the app. Blocking on every inProgress != None used to unmount this whole
     * subtree (Router, Layout, SettingFocusHost, the toast container...) for however long that
     * work took, discarding in-flight app state with no trace. That is what silently killed a
     * settings deep link's pending highlight search mid-wait: the effect watching for the field
     * got torn down by this remount before it ever got the chance to give up honestly with a
     * toast. Gating on NOT having a session yet keeps this covering the real first-load
     * login/redirect flow without nuking the app for every silent renewal after that.
     */
    const hasSession = isAuthenticated && !!auth.token;

    if (inProgress == InteractionStatus.Logout) {
        return <Authenticating message={'Logging Out'} error={logoutMessage}></Authenticating>
    }
    else if(inProgress == InteractionStatus.HandleRedirect && !hasSession) {
        return <Authenticating message={'Redirecting'}></Authenticating>

    }
    else if (inProgress != InteractionStatus.None && !hasSession) {
        return <Authenticating />;
    }

    else {
        return <>{props.children}</>
    }
}

export default AuthHandler;